/**
 * Post Recommend Handler
 * - Orchestration only (no direct DOM mutation except via render)
 */

import { notifier } from "../../core/notifier/notifier";
import { phpMaxRecommendRank } from "../../core/php-data-access";
import { translator } from "../../translation/translator";
import { z } from "zod";
import {
    GetRecommendMetaForPost200Response,
    RemoveRecommendForPost200Response,
    UpsertRecommendForPost200Response,
    UpsertRecommendForPost422Response,
} from "../../api/generated/posts.zod";
import { toPositiveIntOrNull } from "../../util/id-utils";
import { postRecommendForm } from "./post-recommend-form";

interface RecommendApi {
    getMeta(boardId: string, postId: number): Promise<Response>;
    upsert(boardId: string, postId: number, rank: number | null): Promise<Response>;
    remove(boardId: string, postId: number): Promise<Response>;
    clearAll(boardId: string): Promise<Response>;
}

interface RecommendRenderActions {
    onPickRank?: ((rank: number) => Promise<void> | void) | null;
    onUnset?: (() => Promise<void> | void) | null;
    onClearAll?: (() => Promise<void> | void) | null;
}

interface RecommendRender {
    setActions(actions: RecommendRenderActions): void;
    renderBody(vm: RecommendViewModel): void;
    renderLoading(): void;
    open(): void;
    close(): void;
    setModalTitle(boardId: string | null | undefined): void;
    setBusy(busy: boolean): void;
    setCrownState(el: HTMLElement, recommended: boolean): void;
    setCrownRank(el: HTMLElement, rank: number | null): void;
    setCrownLoading?(el: HTMLElement, loading: boolean): void;
}

type RecommendMetaResponse = z.infer<typeof GetRecommendMetaForPost200Response>;
type RecommendMutationSuccessResponse =
    | z.infer<typeof UpsertRecommendForPost200Response>
    | z.infer<typeof RemoveRecommendForPost200Response>;
type RecommendMutationErrorResponse = z.infer<typeof UpsertRecommendForPost422Response>;
type RecommendMutationResponse = RecommendMutationSuccessResponse | RecommendMutationErrorResponse;

interface RecommendClearAllResponse {
    success?: boolean | number | string;
    message?: string;
}

interface RankButtonViewModel {
    rank: number;
    disabled: boolean;
    isCurrent: boolean;
}

interface RecommendViewModel {
    buttons: RankButtonViewModel[];
    canUnset: boolean;
}

interface PostRecommendHandlerDeps {
    api: RecommendApi;
    render: RecommendRender;
}

class PostRecommendHandler {
    public api: RecommendApi;
    public render: RecommendRender;
    public activeBoardId: string | null;
    public activePostId: number | null;
    public activeCrownEl: HTMLElement | null;
    public busy: boolean;

    public constructor(deps: PostRecommendHandlerDeps) {
        this.api = deps.api;
        this.render = deps.render;
        this.activeBoardId = null;
        this.activePostId = null;
        this.activeCrownEl = null;
        this.busy = false;
    }

    public getTranslatedMessage(key: string, fallback: string): string {
        const value = translator?.getTranslation(key);
        return value && value !== key ? value : fallback;
    }

    public isRecommendMetaResponse(value: unknown): value is RecommendMetaResponse {
        return !!value && typeof value === "object" && !Array.isArray(value);
    }

    public isRecommendMetaSuccessResponse(json: unknown): json is RecommendMetaResponse {
        return this.isRecommendMetaResponse(json) && json.success === 1;
    }

    public isRecommendMutationResponse(value: unknown): value is RecommendMutationResponse {
        return !!value && typeof value === "object" && !Array.isArray(value);
    }

    public isRecommendMutationSuccessResponse(json: unknown): json is RecommendMutationSuccessResponse {
        return this.isRecommendMutationResponse(json) && json.success === 1;
    }

    public getResponseMessage(json: unknown): string | null {
        if (!json || typeof json !== "object" || Array.isArray(json)) {
            return null;
        }

        const message = (json as { message?: unknown }).message;
        return typeof message === "string" && message.trim() !== "" ? message : null;
    }

    public async handleRecommendClick(event: MouseEvent, crownEl: HTMLElement): Promise<void> {
        event.preventDefault();
        event.stopPropagation();

        const boardId = String(crownEl.dataset.boardId || "").trim();
        const postId = toPositiveIntOrNull(crownEl.dataset.postId);

        if (!boardId) return;
        if (postId === null) return;

        const canRecommend = postRecommendForm.getRecommendCapabilityHost(crownEl) !== null;
        if (!canRecommend) return;

        if (this.busy || crownEl.dataset.loading === "1") return;

        this.activeBoardId = boardId;
        this.activePostId = postId;
        this.activeCrownEl = crownEl;

        this.render.setActions({
            onPickRank: async (rank) => await this.setRank(rank),
            onUnset: async () => await this.unsetRecommend(),
            onClearAll: async () => await this.clearAllRecommends(),
        });

        this.render.setModalTitle(boardId);
        this.render.renderLoading();
        this.render.open();

        try {
            const res = await this.api.getMeta(boardId, postId);
            const json = (await res.json().catch(() => null)) as RecommendMetaResponse | null;
            const ok = res.ok && this.isRecommendMetaSuccessResponse(json);
            const meta = this.isRecommendMetaResponse(json) ? json : null;

            if (!ok || !meta) {
                notifier.error(
                    String(
                        this.getResponseMessage(meta) ||
                            this.getTranslatedMessage("post_recommend_error_load", "Failed to load recommend info"),
                    ),
                );
                this.render.close();
                return;
            }

            this.render.renderBody(this.buildViewModel(meta));
        } catch (error) {
            console.error(error);
            notifier.error(this.getTranslatedMessage("post_recommend_error_load", "Failed to load recommend info"));
            this.render.close();
        }
    }

    public async reloadMeta(): Promise<void> {
        if (!this.activeBoardId || !this.activePostId) return;

        this.render.renderLoading();

        try {
            const res = await this.api.getMeta(this.activeBoardId, this.activePostId);
            const json = (await res.json().catch(() => null)) as RecommendMetaResponse | null;
            const ok = res.ok && this.isRecommendMetaSuccessResponse(json);
            const meta = this.isRecommendMetaResponse(json) ? json : null;

            if (!ok || !meta) {
                notifier.error(
                    String(
                        this.getResponseMessage(meta) ||
                            this.getTranslatedMessage("post_recommend_error_load", "Failed to load recommend info"),
                    ),
                );
                this.render.close();
                return;
            }

            this.render.renderBody(this.buildViewModel(meta));
        } catch (error) {
            console.error(error);
            notifier.error(this.getTranslatedMessage("post_recommend_error_load", "Failed to load recommend info"));
            this.render.close();
        }
    }

    public findAllCrowns(boardId: string, postId: number): HTMLElement[] {
        return postRecommendForm.getRecommendButtons(document, boardId, postId);
    }

    public findAllCrownsInBoard(boardId: string): HTMLElement[] {
        return postRecommendForm.getRecommendButtons(document, boardId);
    }

    public async setRank(rank: number): Promise<void> {
        if (!this.activeBoardId || !this.activePostId || !this.activeCrownEl) return;

        this.render.renderLoading();
        this.busy = true;
        this.render.setBusy(true);
        try {
            const res = await this.api.upsert(this.activeBoardId, this.activePostId, rank);
            const json = (await res.json().catch(() => null)) as RecommendMutationResponse | null;
            if (!res.ok || !this.isRecommendMutationSuccessResponse(json)) {
                notifier.error(
                    String(
                        this.getResponseMessage(json) ||
                            this.getTranslatedMessage("post_recommend_error_update", "Failed to update recommend rank"),
                    ),
                );
                await this.reloadMeta();
                return;
            }

            const crowns = this.findAllCrowns(this.activeBoardId, this.activePostId);
            crowns.forEach((el) => {
                this.render.setCrownState(el, true);
                this.render.setCrownRank(el, rank);
            });

            this.render.close();
        } catch (error) {
            console.error(error);
            notifier.error(this.getTranslatedMessage("post_recommend_error_update", "Failed to update recommend rank"));
            await this.reloadMeta();
        } finally {
            this.render.setBusy(false);
            this.busy = false;
        }
    }

    public async unsetRecommend(): Promise<void> {
        if (!this.activeBoardId || !this.activePostId || !this.activeCrownEl) return;

        this.render.renderLoading();
        this.busy = true;
        this.render.setBusy(true);
        try {
            const res = await this.api.remove(this.activeBoardId, this.activePostId);
            const json = (await res.json().catch(() => null)) as RecommendMutationResponse | null;
            if (!res.ok || !this.isRecommendMutationSuccessResponse(json)) {
                notifier.error(
                    String(
                        this.getResponseMessage(json) ||
                            this.getTranslatedMessage("post_recommend_error_unset", "Failed to unset recommend"),
                    ),
                );
                await this.reloadMeta();
                return;
            }

            const crowns = this.findAllCrowns(this.activeBoardId, this.activePostId);
            crowns.forEach((el) => {
                this.render.setCrownState(el, false);
                this.render.setCrownRank(el, null);
            });

            this.render.close();
        } catch (error) {
            console.error(error);
            notifier.error(this.getTranslatedMessage("post_recommend_error_unset", "Failed to unset recommend"));
            await this.reloadMeta();
        } finally {
            this.render.setBusy(false);
            this.busy = false;
        }
    }

    public async clearAllRecommends(): Promise<void> {
        if (!this.activeBoardId) return;

        const confirmMessage = this.getTranslatedMessage(
            "post_recommend_remove_all_confirm",
            "Remove all recommended ranks?",
        );
        const ok = await notifier.confirm(confirmMessage, {
            okTranslateKey: "remove_all",
            okText: this.getTranslatedMessage("remove_all", "Remove All"),
            cancelText: this.getTranslatedMessage("cancel", "Cancel"),
        });
        if (!ok) {
            return;
        }

        this.render.renderLoading();
        this.busy = true;
        this.render.setBusy(true);
        try {
            const res = await this.api.clearAll(this.activeBoardId);
            const json = (await res.json().catch(() => null)) as RecommendClearAllResponse | null;
            const okRes = res.ok && !!json && (json.success === true || json.success === 1 || json.success === "1");

            if (!okRes) {
                notifier.error(
                    String(
                        this.getResponseMessage(json) ||
                            this.getTranslatedMessage("post_recommend_error_clear", "Failed to clear recommends"),
                    ),
                );
                await this.reloadMeta();
                return;
            }

            const crowns = this.findAllCrownsInBoard(this.activeBoardId);
            crowns.forEach((el) => {
                this.render.setCrownState(el, false);
                this.render.setCrownRank(el, null);
            });

            this.render.close();
        } catch (error) {
            console.error(error);
            notifier.error(this.getTranslatedMessage("post_recommend_error_clear", "Failed to clear recommends"));
            await this.reloadMeta();
        } finally {
            this.render.setBusy(false);
            this.busy = false;
        }
    }

    private buildViewModel(meta: RecommendMetaResponse): RecommendViewModel {
        const maxRank = Number(meta.max_rank || phpMaxRecommendRank());
        const usedRanks = Array.isArray(meta.used_ranks) ? meta.used_ranks : [];
        const currentRank =
            meta.current_rank === null || typeof meta.current_rank === "undefined" ? null : Number(meta.current_rank);

        const used = new Set(usedRanks.map((n) => Number(n)).filter((n) => Number.isFinite(n)));

        const buttons: RankButtonViewModel[] = [];
        for (let r = 1; r <= maxRank; r++) {
            const isUsed = used.has(r);
            const isCurrent = currentRank !== null && r === currentRank;
            buttons.push({
                rank: r,
                disabled: isUsed && !isCurrent,
                isCurrent,
            });
        }

        return {
            buttons,
            canUnset: currentRank !== null,
        };
    }
}

export { PostRecommendHandler };
